import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { Phone, Pencil, ArrowLeft } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { StatusBadge } from '@/components/StatusBadge';
import { leadsApi } from '@/lib/api/endpoints';
import {
  CALL_STATUS_LABEL,
  CALL_STATUS_VARIANT,
  LEAD_STATUS_LABEL,
  LEAD_STATUS_VARIANT,
  OUTCOME_LABEL,
  fmtDateTime,
  fmtDuration,
  fmtPhone,
  fmtUsd,
} from '@/lib/format';
import { CallNowDialog } from './CallNowDialog';
import { LeadFormDialog } from './LeadFormDialog';
import { useCan } from '@/features/auth/store';

export function LeadDetailPage() {
  const { id = '' } = useParams();
  const q = useQuery({ queryKey: ['leads', id], queryFn: () => leadsApi.get(id) });
  const [callOpen, setCallOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const canEdit = useCan('leads.manage');
  const canCall = useCan('leads.call');
  const lead = q.data;

  if (q.isLoading) return <Skeleton className="h-64 w-full" />;
  if (!lead) return <p className="text-muted-foreground">Lead não encontrado.</p>;

  const raw = (lead as unknown as { rawBody?: Record<string, unknown> }).rawBody;

  return (
    <div>
      <PageHeader
        title={lead.name || 'Lead sem nome'}
        description={`${fmtPhone(lead.phoneE164)}${lead.phoneValid === false ? ' · TELEFONE INVÁLIDO (não será discado)' : ''} · ${lead.email ?? 'sem e-mail'}`}
        actions={
          <>
            <Button variant="ghost" asChild>
              <Link to="/leads">
                <ArrowLeft /> Voltar
              </Link>
            </Button>
            {canEdit && (
              <Button variant="outline" onClick={() => setEditOpen(true)}>
                <Pencil /> Editar
              </Button>
            )}
            {canCall && (
              <Button
                onClick={() => setCallOpen(true)}
                disabled={lead.status === 'do_not_call' || lead.status === 'calling'}
              >
                <Phone /> Ligar agora
              </Button>
            )}
          </>
        }
      />

      <div className="grid gap-4 lg:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Dados do lead</CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
              <dt className="text-muted-foreground">Status</dt>
              <dd>
                <StatusBadge
                  label={LEAD_STATUS_LABEL[lead.status]}
                  className={LEAD_STATUS_VARIANT[lead.status]}
                />
              </dd>
              <dt className="text-muted-foreground">Produto</dt>
              <dd>
                {lead.product?.name ?? lead.productCodename ?? '—'}{' '}
                <span className="text-xs text-muted-foreground">{lead.productCodename}</span>
              </dd>
              <dt className="text-muted-foreground">Origem</dt>
              <dd>{lead.sourceSlug ?? '—'}</dd>
              <dt className="text-muted-foreground">Afiliado</dt>
              <dd>{lead.affId ?? '—'}</dd>
              <dt className="text-muted-foreground">País</dt>
              <dd>
                {lead.country ?? '—'}{' '}
                {lead.countryIso && (
                  <span className="text-xs text-muted-foreground">({lead.countryIso})</span>
                )}
              </dd>
              <dt className="text-muted-foreground">Cidade/UF</dt>
              <dd>{[lead.city, lead.state].filter(Boolean).join(', ') || '—'}</dd>
              <dt className="text-muted-foreground">Endereço</dt>
              <dd>{lead.address ?? '—'}</dd>
              <dt className="text-muted-foreground">Telefone bruto</dt>
              <dd className="font-mono text-xs">{lead.phoneRaw}</dd>
              <dt className="text-muted-foreground">Tentativas</dt>
              <dd>{lead.attempts}</dd>
              <dt className="text-muted-foreground">Recebido em</dt>
              <dd>{fmtDateTime(lead.receivedAt)}</dd>
              <dt className="text-muted-foreground">Última ligação</dt>
              <dd>{fmtDateTime(lead.lastCallAt)}</dd>
              <dt className="text-muted-foreground">ID externo</dt>
              <dd className="font-mono text-xs">{lead.externalId ?? '—'}</dd>
            </dl>
            {raw && (
              <details className="mt-4">
                <summary className="cursor-pointer text-xs text-muted-foreground">
                  Payload original (raw_body)
                </summary>
                <pre className="mt-2 max-h-48 overflow-auto rounded bg-muted p-2 text-[11px]">
                  {JSON.stringify(raw, null, 2)}
                </pre>
              </details>
            )}
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-base">Histórico de chamadas</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {lead.calls.length ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Data</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Resultado</TableHead>
                    <TableHead>Duração</TableHead>
                    <TableHead>Custo</TableHead>
                    <TableHead>Idioma</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {lead.calls.map((c) => (
                    <TableRow key={c.id}>
                      <TableCell>
                        <Link
                          to={`/chamadas/${c.id}`}
                          className="underline-offset-2 hover:underline"
                        >
                          {fmtDateTime(c.createdAt)}
                        </Link>
                      </TableCell>
                      <TableCell>
                        <StatusBadge
                          label={CALL_STATUS_LABEL[c.status]}
                          className={CALL_STATUS_VARIANT[c.status]}
                        />
                      </TableCell>
                      <TableCell>{c.outcome ? OUTCOME_LABEL[c.outcome] : '—'}</TableCell>
                      <TableCell>{fmtDuration(c.durationSec)}</TableCell>
                      <TableCell>{fmtUsd(c.costUsd)}</TableCell>
                      <TableCell>{c.language}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <p className="p-6 text-sm text-muted-foreground">Nenhuma ligação ainda.</p>
            )}
          </CardContent>
        </Card>
      </div>

      <CallNowDialog lead={callOpen ? lead : null} onOpenChange={setCallOpen} />
      <LeadFormDialog open={editOpen} onOpenChange={setEditOpen} lead={lead} />
    </div>
  );
}
